import React from 'react';
import { useAsync } from 'react-use';
import { useDebounce } from 'use-debounce';
import { useIntl } from 'react-intl';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { darken } from '@mui/material/styles';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import {
  ArrowLeft,
  ChevronDown,
  Edit2,
  SparkleCircle,
  X,
} from '@lifeomic/chromicons';
import {
  useActiveProjectId,
  useActiveProjectCohortId,
  useActiveUserId,
  useFeatures,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useMaskedMode } from '@lifeomic/phc-web-toolkit/src/hooks/useMaskedMode';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import SubjectSearchService from '@lifeomic/phc-web-toolkit/src/services/SubjectSearchService';
import { getNameFromFHIRRecord } from '@lifeomic/phc-web-toolkit/src/util/fhir';
import {
  getRecentConversationKey,
  useRecentConversations,
} from '../hooks/useRecentConversations';
import {
  safeGetPersistedValue,
  safePersistValue,
} from '@lifeomic/phc-web-toolkit/src/hooks/useLocalStorageState';
import {
  CLINICIANS_AGENT_USERNAME,
  POPULATION_AGENT_USERNAME,
} from '../constants';
import { ChatDockTab } from './ChatDockTab';
import {
  PatientSuggestion,
  RecentChatsPanel,
  StartChatPanel,
} from './ChatDockPanels';
import {
  ConversationEntry,
  FhirSubjectSearch,
  ensurePopulationConversationId,
  resolvePatientIdFromConversation,
} from './chatDockUtils';
import { chatDockMessages } from './chatDockMessages';

const subjectSearchService = new SubjectSearchService();

const CHAT_LIST_WIDTH = 360;
const CHAT_TAB_WIDTH = 468;
const CHAT_GAP = 12;
const OPEN_CHAT_KEYS_STORAGE_KEY = 'zoriOpenChatKeysV1';
const COLLAPSED_CHAT_KEYS_STORAGE_KEY = 'zoriCollapsedChatKeysV1';
const FULLSCREEN_CHAT_KEY_STORAGE_KEY = 'zoriFullscreenChatKeyV1';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'fixed',
    right: 0,
    bottom: 0,
    zIndex: theme.zIndex.modal,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
  },
  dockButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderRadius: theme.spacing(4),
    width: theme.spacing(6),
    height: theme.spacing(6),
    boxShadow: theme.boxShadows.table,
    '&:hover': {
      backgroundColor: darken(theme.palette.primary.main, 0.1),
    },
  },
  panel: {
    width: theme.pxToRem(CHAT_LIST_WIDTH),
    marginRight: theme.pxToRem(CHAT_GAP),
    height: theme.pxToRem(520),
    backgroundColor: theme.palette.common.white,
    borderTopLeftRadius: theme.spacing(1),
    borderTopRightRadius: theme.spacing(1),
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    boxShadow: theme.boxShadows.table,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: theme.transitions.create('height', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  panelCollapsed: {
    height: theme.pxToRem(48),
  },
  panelBody: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    transition: theme.transitions.create(['opacity', 'visibility'], {
      duration: theme.transitions.duration.shortest,
    }),
  },
  panelBodyHidden: {
    opacity: 0,
    visibility: 'hidden',
    pointerEvents: 'none',
  },
  header: {
    minHeight: theme.pxToRem(48),
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  headerTitleIcon: {
    color: theme.palette.primary.main,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  conversationList: {
    flex: 1,
    overflowY: 'auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  createChatPanel: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  createChatHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  conversationItem: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    width: '100%',
    height: theme.pxToRem(72),
    minHeight: theme.pxToRem(72),
    maxHeight: theme.pxToRem(72),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: '0 0 auto',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:hover $conversationRemove': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
  conversationItemDisabled: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  conversationMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  conversationMetaActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  conversationRemove: {
    opacity: 0,
    pointerEvents: 'none',
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  messagePreview: {
    color: theme.palette.text.secondary,
  },
  unreadDot: {
    width: theme.spacing(1),
    height: theme.spacing(1),
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    marginLeft: theme.spacing(1),
  },
  emptyState: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  chatContainer: {
    flex: 1,
    minHeight: 0,
  },
  tabContainer: {
    position: 'fixed',
    right: 0,
    bottom: 0,
    width: theme.pxToRem(CHAT_TAB_WIDTH),
    height: theme.pxToRem(520),
    backgroundColor: theme.palette.common.white,
    borderTopLeftRadius: theme.spacing(1),
    borderTopRightRadius: theme.spacing(1),
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    boxShadow: theme.boxShadows.table,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: theme.zIndex.modal,
    transition: theme.transitions.create('height', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  tabContainerFullscreen: {
    right: 0,
    bottom: 0,
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    borderRadius: 0,
  },
  tabContainerCollapsed: {
    height: theme.pxToRem(48),
  },
  tabHeader: {
    minHeight: theme.pxToRem(48),
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  tabTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tabActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));

export const ChatDock = () => {
  const classes = useStyles({});
  const { formatMessage, formatDate, formatTime } = useIntl();
  const hasFeatures = useFeatures();
  const { recentConversations, loading, hideConversation, unhideConversation } =
    useRecentConversations();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isCreatingChat, setIsCreatingChat] = React.useState(false);
  const [openConversationKeys, setOpenConversationKeys] = React.useState<
    string[]
  >(() => {
    const stored =
      safeGetPersistedValue<string[]>(OPEN_CHAT_KEYS_STORAGE_KEY) || [];
    return stored.filter(key => typeof key === 'string' && key.length > 0);
  });
  const [localConversationMap, setLocalConversationMap] = React.useState<
    Record<string, ConversationEntry>
  >({});
  const [fullscreenKey, setFullscreenKey] = React.useState<string | null>(
    () => {
      const stored = safeGetPersistedValue<string | null>(
        FULLSCREEN_CHAT_KEY_STORAGE_KEY,
      );
      return typeof stored === 'string' && stored.length > 0 ? stored : null;
    },
  );
  const [collapsedKeys, setCollapsedKeys] = React.useState<string[]>(() => {
    const stored =
      safeGetPersistedValue<string[]>(COLLAPSED_CHAT_KEYS_STORAGE_KEY) || [];
    return stored.filter(key => typeof key === 'string' && key.length > 0);
  });
  const activeUserId = useActiveUserId();
  const activeProjectId = useActiveProjectId();
  const activeProjectCohortId = useActiveProjectCohortId();
  const maskedMode = useMaskedMode();
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedPatient, setSelectedPatient] =
    React.useState<PatientSuggestion | null>(null);
  const [debouncedFilter] = useDebounce(searchValue, 500);
  const [fhirSubjectSearch] = useService(
    subjectSearchService.fhirSubjectSearch,
    {
      id: 'zori-chat-subject-search',
      name: 'zori-chat-subject-search',
      log: { start: true, success: true, error: true },
      rethrowErrors: true,
    },
  );

  const openConversations = React.useMemo(
    () =>
      openConversationKeys
        .map(key => {
          const recent = recentConversations.find(
            conversation => conversation.key === key,
          );
          return recent || localConversationMap[key];
        })
        .filter((conversation): conversation is ConversationEntry =>
          Boolean(conversation),
        ),
    [openConversationKeys, recentConversations, localConversationMap],
  );
  const hasFullscreenConversation = React.useMemo(
    () =>
      Boolean(
        fullscreenKey &&
          openConversations.some(
            conversation => conversation.key === fullscreenKey,
          ),
      ),
    [fullscreenKey, openConversations],
  );

  React.useEffect(() => {
    safePersistValue(OPEN_CHAT_KEYS_STORAGE_KEY, openConversationKeys);
  }, [openConversationKeys]);

  React.useEffect(() => {
    safePersistValue(COLLAPSED_CHAT_KEYS_STORAGE_KEY, collapsedKeys);
  }, [collapsedKeys]);

  React.useEffect(() => {
    safePersistValue(FULLSCREEN_CHAT_KEY_STORAGE_KEY, fullscreenKey);
  }, [fullscreenKey]);

  const { value: patientResults, loading: isSearching } = useAsync(async () => {
    if (maskedMode || !debouncedFilter) return [];
    const trimmed = debouncedFilter.trim();
    if (trimmed.length < 3) return [];
    const res = await fhirSubjectSearch({
      projectId: activeProjectId,
      body: {
        name: trimmed.split(/\s+/),
      },
    });
    if (res.data) {
      return res.data.entry.map(entry => entry.resource);
    }
    return [];
  }, [debouncedFilter, maskedMode, activeProjectId]);

  const patientSuggestions = React.useMemo(() => {
    const patients = (patientResults || []) as fhir3.Patient[];
    return patients
      .map(patient => {
        const name = getNameFromFHIRRecord(patient.name);
        return {
          id: patient.id!,
          display: `${name.given} ${name.family}`.trim(),
          patient,
        };
      })
      .filter(patient => patient.id && patient.display);
  }, [patientResults]);

  const renderConversationPreview = (timestamp?: string) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return `${formatDate(date, {
      month: 'short',
      day: 'numeric',
    })} ${formatTime(date, { hour: 'numeric', minute: '2-digit' })}`;
  };

  const openConversation = React.useCallback(
    (entry: {
      conversationId?: string;
      patientId?: string;
      title?: string;
      userIds?: string[];
    }) => {
      const resolvedPatientId = resolvePatientIdFromConversation({
        patientId: entry.patientId,
        userIds: entry.userIds,
      });
      const userIds =
        resolvedPatientId && activeUserId
          ? [
              activeUserId,
              CLINICIANS_AGENT_USERNAME,
              `scope?subject=${resolvedPatientId}`,
            ]
          : entry.userIds?.length
          ? entry.userIds
          : undefined;
      const normalizedUserIds = ensurePopulationConversationId(userIds);
      const normalizedEntry = {
        conversationId: entry.conversationId || '',
        patientId: resolvedPatientId,
        title: entry.title,
        userIds: normalizedUserIds,
      };
      const key =
        normalizedEntry.conversationId ||
        getRecentConversationKey(normalizedEntry);
      if (!key) return;
      unhideConversation(entry.conversationId);
      setLocalConversationMap(prev => ({
        ...prev,
        [key]: {
          key,
          conversationId: normalizedEntry.conversationId || undefined,
          patientId: normalizedEntry.patientId,
          title: normalizedEntry.title,
          userIds: normalizedEntry.userIds,
        },
      }));
      setIsCreatingChat(false);
      setOpenConversationKeys(prev => {
        const next = [key, ...prev.filter(existing => existing !== key)];
        return next.slice(0, 2);
      });
    },
    [activeUserId, unhideConversation],
  );

  const handleCreateChatClick = () => {
    setIsCreatingChat(true);
    setSearchValue('');
    setSelectedPatient(null);
  };

  const handleHideConversation = React.useCallback(
    (conversation: ConversationEntry) => {
      hideConversation(conversation.conversationId);
      setOpenConversationKeys(keys =>
        keys.filter(key => key !== conversation.key),
      );
      setLocalConversationMap(prev => {
        if (!prev[conversation.key]) return prev;
        const next = { ...prev };
        delete next[conversation.key];
        return next;
      });
      setFullscreenKey(prev => (prev === conversation.key ? null : prev));
      setCollapsedKeys(keys => keys.filter(key => key !== conversation.key));
    },
    [hideConversation],
  );

  const handleSuggestionSelected = (selected: {
    display: string;
    suggestionValue?: string;
    suggestion: PatientSuggestion;
  }) => {
    if (!activeUserId) return;
    const { suggestion, display } = selected;
    setSelectedPatient(suggestion);
    const userIds = [
      activeUserId,
      CLINICIANS_AGENT_USERNAME,
      `scope?subject=${suggestion.id}`,
    ];
    openConversation({
      patientId: suggestion.id,
      title: display || suggestion.display,
      userIds,
    });
  };

  const handleStartWithoutPatient = () => {
    if (!activeUserId) return;
    const userIds = [
      activeUserId,
      POPULATION_AGENT_USERNAME,
      `scope?conversation_id=${Date.now()}`,
    ];
    openConversation({
      title: formatMessage(chatDockMessages.populationChatTitle),
      userIds,
    });
  };

  if (!hasFeatures('clinicianAgentChat', 'clinicianAgentChatGlobal'))
    return null;

  return (
    <Box className={classes.root}>
      <Box
        className={`${classes.panel}${
          isOpen ? '' : ` ${classes.panelCollapsed}`
        }`}
      >
        <Box
          className={classes.header}
          role={!isOpen ? 'button' : undefined}
          tabIndex={!isOpen ? 0 : undefined}
          onClick={() => {
            if (!isOpen) {
              setIsOpen(true);
            }
          }}
          onKeyDown={event => {
            if (isOpen) return;
            if (event.key === 'Enter' || event.key === ' ') {
              setIsOpen(true);
            }
          }}
        >
          <Box className={classes.headerTitle}>
            {isCreatingChat ? (
              <IconButton
                aria-label={formatMessage(chatDockMessages.back)}
                icon={ArrowLeft}
                size={0}
                onClick={() => {
                  setIsCreatingChat(false);
                }}
              />
            ) : null}
            <SparkleCircle className={classes.headerTitleIcon} />
            <Text size="subbody" weight="bold">
              {formatMessage(chatDockMessages.headerTitle)}
            </Text>
          </Box>
          {isOpen && (
            <Box className={classes.headerActions}>
              {!isCreatingChat && (
                <Tooltip title={formatMessage(chatDockMessages.newChat)}>
                  <IconButton
                    aria-label={formatMessage(chatDockMessages.newChat)}
                    icon={Edit2}
                    size={0}
                    onClick={handleCreateChatClick}
                  />
                </Tooltip>
              )}
              <IconButton
                aria-label={formatMessage(chatDockMessages.close)}
                icon={ChevronDown}
                size={0}
                onClick={() => {
                  setIsOpen(false);
                  setIsCreatingChat(false);
                }}
              />
              <IconButton
                aria-label={formatMessage(chatDockMessages.close)}
                icon={X}
                size={0}
                onClick={() => {
                  setIsOpen(false);
                  setIsCreatingChat(false);
                }}
              />
            </Box>
          )}
        </Box>
        <Box
          className={`${classes.panelBody}${
            isOpen ? '' : ` ${classes.panelBodyHidden}`
          }`}
        >
          {isCreatingChat ? (
            <StartChatPanel
              classes={{
                createChatPanel: classes.createChatPanel,
                createChatHeader: classes.createChatHeader,
              }}
              maskedMode={maskedMode}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              isSearching={isSearching}
              patientSuggestions={patientSuggestions}
              selectedPatient={selectedPatient}
              handleSuggestionSelected={handleSuggestionSelected}
              handleStartWithoutPatient={handleStartWithoutPatient}
              isStartDisabled={!activeUserId}
            />
          ) : (
            <RecentChatsPanel
              classes={{
                conversationList: classes.conversationList,
                emptyState: classes.emptyState,
              }}
              rowClasses={{
                conversationItem: classes.conversationItem,
                conversationItemDisabled: classes.conversationItemDisabled,
                conversationMeta: classes.conversationMeta,
                conversationMetaActions: classes.conversationMetaActions,
                conversationRemove: classes.conversationRemove,
              }}
              recentConversations={recentConversations}
              loading={loading}
              renderConversationPreview={renderConversationPreview}
              openConversation={openConversation}
              handleHideConversation={handleHideConversation}
              maskedMode={maskedMode}
              fhirSubjectSearch={fhirSubjectSearch as FhirSubjectSearch}
              activeProjectId={activeProjectId}
              activeProjectCohortId={activeProjectCohortId || undefined}
            />
          )}
        </Box>
      </Box>
      {openConversations.map((conversation, index) => {
        return (
          <ChatDockTab
            key={conversation.key}
            conversation={conversation}
            index={index}
            chatGap={CHAT_GAP}
            chatListWidth={CHAT_LIST_WIDTH}
            chatTabWidth={CHAT_TAB_WIDTH}
            classes={{
              tabContainer: classes.tabContainer,
              tabContainerCollapsed: classes.tabContainerCollapsed,
              tabContainerFullscreen: classes.tabContainerFullscreen,
              tabHeader: classes.tabHeader,
              tabTitle: classes.tabTitle,
              tabActions: classes.tabActions,
              chatContainer: classes.chatContainer,
              emptyState: classes.emptyState,
            }}
            maskedMode={maskedMode}
            fhirSubjectSearch={fhirSubjectSearch as FhirSubjectSearch}
            activeProjectId={activeProjectId}
            activeProjectCohortId={activeProjectCohortId || undefined}
            fullscreenKey={fullscreenKey}
            collapsedKeys={collapsedKeys}
            setCollapsedKeys={setCollapsedKeys}
            setFullscreenKey={setFullscreenKey}
            setOpenConversationKeys={setOpenConversationKeys}
            setLocalConversationMap={setLocalConversationMap}
            hasFullscreenConversation={hasFullscreenConversation}
          />
        );
      })}
    </Box>
  );
};

export default React.memo(ChatDock);
